<script setup lang="ts">
import { AudioLines, BotMessageSquare, Brain, Crown, Ear, FlaskConical, ImageUpscale, Palette, Search, SquareStack, Terminal, Users, VenetianMask, WandSparkles } from 'lucide-vue-next'
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfirm } from 'primevue/useconfirm'
import Testimonials from '../subscription/Testimonials.vue'
import AnimatedBackground from '../utils/AnimatedBackground.vue'
import RecreateImages from '@/components/promo_pages/RecreateImages.vue'
import Footer from '@/components/Footer.vue'
import PromoBlock from '@/components/promo_pages/PromoBlock.vue'
import PromoHeader from '@/components/promo_pages/PromoHeader.vue'

const { t } = useI18n()

onMounted(() => {
	document.documentElement.classList.add('scroll-smooth')
})

onBeforeUnmount(() => {
	document.documentElement.classList.remove('scroll-smooth')
})

const recreateImages = ref({
	girl: [
		{ uuid: '983de4f4-5040-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: 'e0be2248-5040-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: '218b716c-5042-11f0-ad53-1aa960dcfa70', nsfw: false },
		{ uuid: '8a3b9444-5042-11f0-ad53-1aa960dcfa70', nsfw: false },
		{ uuid: '2b0f25ac-5043-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: '5e14af3c-5041-11f0-8e2c-1aa960dcfa70', nsfw: true },
		{ uuid: 'a9b68e2c-5043-11f0-ad53-1aa960dcfa70', nsfw: true },
		{ uuid: 'c605e31a-5044-11f0-ad53-1aa960dcfa70', nsfw: true },
		{ uuid: '1d093f18-5045-11f0-8e2c-1aa960dcfa70', nsfw: true },
		{ uuid: '9f10d6d8-5045-11f0-8e2c-1aa960dcfa70', nsfw: true },
		{ uuid: 'ff369782-5045-11f0-ad53-1aa960dcfa70', nsfw: true },
	],
	man: [
		{ uuid: '39689c66-5046-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: 'a8c79bde-5046-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: 'd520ba1c-5046-11f0-8e2c-1aa960dcfa70', nsfw: false },
		{ uuid: '2498f7da-5047-11f0-8e2c-1aa960dcfa70', nsfw: true },
		{ uuid: 'bb6268e0-5047-11f0-ad53-1aa960dcfa70', nsfw: true },
		{ uuid: '34735c30-5048-11f0-8e2c-1aa960dcfa70', nsfw: true },
		{ uuid: 'b8dee2d2-5048-11f0-bc77-1aa960dcfa70', nsfw: true },
	],
	fetish: [
		{ uuid: '30ac1f6e-5049-11f0-bc77-1aa960dcfa70', nsfw: true, description: t('promo.blocks.possibilities.fetishes.pregnant') },
		{ uuid: 'b72cd11e-5049-11f0-8e2c-1aa960dcfa70', nsfw: true, description: t('promo.blocks.possibilities.fetishes.bdsm') },
		{ uuid: '7a30f582-504a-11f0-ad53-1aa960dcfa70', nsfw: true, description: t('promo.blocks.possibilities.fetishes.interracial') },
		{ uuid: 'dcbd5484-504a-11f0-ad53-1aa960dcfa70', nsfw: true, description: t('promo.blocks.possibilities.fetishes.rough_sex') },
	],
})
</script>

<template>
	<div class="w-full px-4 sm:p-0 sm:w-11/12 2xl:w-8/12 flex mx-auto flex-col gap-[100px] mt-[120px]">
		<AnimatedBackground />
		<div class="w-full flex flex-col gap-10 items-center min-h-[70dvh]">
			<PromoHeader
				position="center"
				type="realistic"
			/>
		</div>

		<RecreateImages :data="recreateImages" />
		<PromoBlock
			id="recreate-images"
			class="scroll-mt-[100px]"
			position="right"
		>
			<template #image>
				<img
					src="/image/promo/create_girl_r.webp"
					class="w-full h-full object-contain rounded-xl"
					alt="A beautiful redhead girl bent over"
					loading="lazy"
				>
				<div class="select-none tracking-tight inline-flex flex-col gap-1 text-sm absolute bottom-1 border-[1px] border-solid border-slate-500 left-1/2 -translate-x-1/2 bg-slate-900/70 backdrop-blur-sm p-2 rounded-xl">
					<div class="flex items-center gap-1 font-bold">
						<WandSparkles class="lucide-button-icon-xs" />
						<span>{{ t("generator.prompt") }}</span>
					</div>
					<span class=" text-slate-200 flex items-center gap-1 whitespace-nowrap">redhead, bent over, particles, neon<div class="h-[16px] w-[1px] bg-white animate-caret-blink" /></span>
				</div>
			</template>
			<template #icon>
				<BotMessageSquare class="-scale-x-100 lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.generate.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.generate.description") }}
			</template>
		</PromoBlock>

		<Testimonials class="flex-center pb-20" />
		<PromoBlock position="right">
			<template #image>
				<img
					src="/image/promo/modifiers_r.webp"
					class="w-full h-full object-contain rounded-xl"
					loading="lazy"
					alt="Image showcase modifiers."
				>
			</template>
			<template #icon>
				<FlaskConical class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.boosters.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.boosters.description") }}
			</template>
		</PromoBlock>

		<PromoBlock
			position="left"
			:reverse="true"
		>
			<template #image>
				<video
					autoplay
					loop
					muted
					playsinline
					src="/gif/search_demo.webm"
					type="video/webm"
					class="h-[400px] object-contain rounded-xl"
					loading="lazy"
					alt="video showcase the faplab search system"
				/>
			</template>
			<template #icon>
				<Search class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.search.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.search.description") }}
			</template>
		</PromoBlock>

		<PromoBlock position="right">
			<template #image>
				<img
					src="/image/promo/auto_completion_r.webp"
					class="w-full h-full object-contain rounded-xl"
					loading="lazy"
					alt="Image showcase AI auto completion"
				>
			</template>
			<template #icon>
				<Brain class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.autocomplete.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.autocomplete.description") }}
			</template>
		</PromoBlock>

		<PromoBlock
			position="left"
			:reverse="true"
		>
			<template #image>
				<img
					loading="lazy"
					src="/image/promo/natural_language_r.webp"
					class="w-full h-full object-contain rounded-xl"
					alt="Image showcase natural language"
				>
				<div class="select-none tracking-tight flex flex-col gap-1 text-sm absolute -bottom-8 border-[1px] border-solid border-slate-500 left-1/2 -translate-x-1/2 bg-slate-900/70 backdrop-blur-sm p-2 rounded-xl">
					<div class="flex items-center gap-1 font-bold">
						<WandSparkles class="lucide-button-icon-xs" />
						<span>{{ t("generator.prompt") }}</span>
					</div>
					<span class=" text-slate-200 w-[300px] sm:w-[360px]">Smiling young woman with brown hair in a white crop top, standing on a lively city street at night, hands behind head |</span>
				</div>
			</template>
			<template #icon>
				<Ear class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.natural.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.natural.description") }}
			</template>
		</PromoBlock>

		<PromoBlock position="right">
			<template #image>
				<img
					loading="lazy"
					src="/image/promo/clone_r.webp"
					class="w-full h-full object-contain rounded-xl"
					alt="Image showcase cloning feature"
				>
			</template>
			<template #icon>
				<SquareStack class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.clone.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.clone.description") }}
			</template>
		</PromoBlock>

		<PromoBlock
			position="left"
			:reverse="true"
		>
			<template #icon>
				<Crown class="lucide-button-icon-md md:lucide-button-icon-lg" />
			</template>
			<template #title>
				{{ t("promo_realistic.blocks.unlimited.title") }}
			</template>
			<template #description>
				{{ t("promo_realistic.blocks.unlimited.description") }}
			</template>
		</PromoBlock>

		<div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 bg-slate-900 rounded-xl p-4 border-[1px] border-solid border-slate-600">
			<div>
				<h2 class="flex-center w-fit gap-1 tracking-tight font-extrabold">
					<AudioLines class="lucide-button-icon-md md:lucide-button-icon-lg" /> {{ t("promo.features.ai_protection.title") }}
				</h2>
				<p class="leading-relaxed tracking-tighter ">
					{{ t("promo.features.ai_protection.description") }}
				</p>
				<router-link
					to="/account/signup"
					class="tracking-tight flex items-center !text-base md:!text-lg !no-underline !font-bold hyperlink-purple"
				>
					<Terminal class="lucide-button-icon-md" /> {{ t("promo.get_started") }}
				</router-link>
			</div>
			<div>
				<h2 class="flex-center w-fit gap-1">
					<VenetianMask class="lucide-button-icon-md md:lucide-button-icon-lg" /> {{ t("promo.features.private.title") }}
				</h2>
				<p class="leading-relaxed tracking-tighter ">
					{{ t("promo.features.private.description") }}
				</p>
				<router-link
					class="tracking-tight flex items-center !text-base md:!text-lg !no-underline !font-bold hyperlink-purple"
					to="/account/signup"
				>
					<Terminal class="lucide-button-icon-md" /> {{ t("promo.get_started") }}
				</router-link>
			</div>
			<div>
				<h2 class="flex-center w-fit gap-1">
					<ImageUpscale class="lucide-button-icon-md md:lucide-button-icon-lg" /> {{ t("promo.features.upscale.title") }}
				</h2>
				<p class="leading-relaxed tracking-tighter ">
					{{ t("promo.features.upscale.description") }}
				</p>
				<router-link
					class="tracking-tight flex items-center !text-base md:!text-lg !no-underline !font-bold hyperlink-purple"
					to="/account/signup"
				>
					<Terminal class="lucide-button-icon-md" /> {{ t("promo.get_started") }}
				</router-link>
			</div>
			<div>
				<h2 class="flex-center w-fit gap-1">
					<Users class="lucide-button-icon-md md:lucide-button-icon-lg" /> {{ t("promo.features.recruit.title") }}
				</h2>
				<p class="leading-relaxed tracking-tighter ">
					{{ t("promo.features.recruit.description") }}
				</p>
				<router-link
					class="tracking-tight flex items-center !text-base md:!text-lg !no-underline !font-bold hyperlink-purple"
					to="/account/signup"
				>
					<Terminal class="lucide-button-icon-md" /> {{ t("promo.get_started") }}
				</router-link>
			</div>
		</div>
		<Footer />
	</div>
</template>

<style scoped lang="scss">
</style>
